import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import ModalCloseButton from '../modals/ModalCloseButton';
import './RiskScoreInfoModal.css';

/**
 * RiskScoreInfoModal — explains how device risk scores (0–9) are calculated.
 * Used from Admin Analytics → Device Risk Assessment (Top 10) help button.
 * Styled to match AlertHelpModal: clean sections, tables, and key info lists.
 */
function RiskScoreInfoModal({ isOpen, onClose }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const content = (
    <div className="risk-score-help-overlay" onClick={onClose}>
      <div className="risk-score-help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="risk-score-help-header">
          <h2>How Risk Score is Calculated</h2>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>

        <div className="risk-score-help-body">
          <section className="risk-score-help-section">
            <h3>Scoring Factors</h3>
            <p className="risk-score-help-intro">
              Three factors contribute points for a total score from <strong>0 to 9</strong>.
              Complaint severity is checked before quantity.
            </p>
            <table className="risk-score-help-table">
              <thead>
                <tr>
                  <th>Factor</th>
                  <th>Condition</th>
                  <th>Points</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td rowSpan="3"><strong>Maintenance Status</strong><br /><span className="risk-score-help-sub">PPM, Calibration &amp; Compliance</span></td>
                  <td>Any type overdue</td>
                  <td>+3</td>
                </tr>
                <tr>
                  <td>Any type due within 30 days</td>
                  <td>+2</td>
                </tr>
                <tr>
                  <td>All types compliant</td>
                  <td>+0</td>
                </tr>
                <tr>
                  <td rowSpan="6"><strong>Complaint Severity</strong><br /><span className="risk-score-help-sub">Severity checked first</span></td>
                  <td>Any critical complaint</td>
                  <td>+3</td>
                </tr>
                <tr>
                  <td>2 or more major complaints</td>
                  <td>+3</td>
                </tr>
                <tr>
                  <td>1 major complaint</td>
                  <td>+2</td>
                </tr>
                <tr>
                  <td>5 or more minor complaints</td>
                  <td>+2</td>
                </tr>
                <tr>
                  <td>3–4 minor complaints</td>
                  <td>+1</td>
                </tr>
                <tr>
                  <td>0–2 complaints (minor only)</td>
                  <td>+0</td>
                </tr>
                <tr>
                  <td rowSpan="3"><strong>Device Age</strong></td>
                  <td>7+ years</td>
                  <td>+2</td>
                </tr>
                <tr>
                  <td>5–7 years</td>
                  <td>+1</td>
                </tr>
                <tr>
                  <td>Less than 5 years</td>
                  <td>+0</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="risk-score-help-section">
            <h3>Score Interpretation</h3>
            <table className="risk-score-help-table">
              <thead>
                <tr>
                  <th>Score</th>
                  <th>Level</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>0/9</strong></td>
                  <td>Perfect</td>
                  <td>No risk — device is healthy</td>
                </tr>
                <tr>
                  <td><strong>1–2/9</strong></td>
                  <td>Low</td>
                  <td>Routine monitoring</td>
                </tr>
                <tr>
                  <td><strong>3–4/9</strong></td>
                  <td>Medium</td>
                  <td>Monitor closely</td>
                </tr>
                <tr>
                  <td><strong>5–6/9</strong></td>
                  <td>Warning</td>
                  <td>Take action soon</td>
                </tr>
                <tr>
                  <td><strong>7–9/9</strong></td>
                  <td>Critical</td>
                  <td>Requires immediate attention</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="risk-score-help-section">
            <h3>Examples</h3>
            <table className="risk-score-help-table">
              <thead>
                <tr>
                  <th>Device profile</th>
                  <th>Breakdown</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>10-month-old, 1 major complaint, compliant maintenance</td>
                  <td>0 + 2 + 0</td>
                  <td><strong>2/9 (Low)</strong></td>
                </tr>
                <tr>
                  <td>8-year-old, 2 major complaints, overdue PPM</td>
                  <td>3 + 3 + 2</td>
                  <td><strong>8/9 (Critical)</strong></td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="risk-score-help-section">
            <h3>Key Information</h3>
            <ul>
              <li><strong>Severity-aware:</strong> 1 major complaint = 2 points; 5 minor complaints = 2 points</li>
              <li><strong>Maintenance first:</strong> Overdue maintenance (including regulatory compliance) is the highest-priority factor</li>
              <li><strong>Critical overrides:</strong> Any critical complaint overrides other complaint rules</li>
              <li><strong>New devices:</strong> Under 5 years old get no age risk points</li>
              <li><strong>Perfect score:</strong> A new device with no issues scores 0/9</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default RiskScoreInfoModal;
